import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { datadogRum } from "@datadog/browser-rum";
import { loadUserProfile } from "components/base-components/MagicBell/profileSession";
import { setDatadogUser } from "utils/datadogUser";

const createInitialProfileState = () => ({
  user: null,
  isLoading: true,
  error: null,
});

const buildLogoutUrl = (returnUrl) =>
  returnUrl
    ? `/logout?returnUrl=${encodeURIComponent(returnUrl)}`
    : "/logout";

const renderProtectedChildren = (children, profileState) =>
  typeof children === "function" ? children(profileState) : children;

const shouldHoldProtectedShell = (user, router) =>
  Boolean(
    user?.is_supplier &&
      !user.is_tos_accepted &&
      (!router.isReady || router.pathname !== "/accept-tos"),
  );

const startSignInSession = (user) => {
  setDatadogUser(user);
};

// ref: https://github.com/ivandotv/nextjs-client-signin-logic/blob/main/src/components/AuthGuard.tsx
export default function AuthGuard({ children }) {
  const [profileState, setProfileState] = useState(createInitialProfileState);
  const router = useRouter();
  const isBlockedBySupplierTos = shouldHoldProtectedShell(
    profileState.user,
    router,
  );

  useEffect(() => {
    let isMounted = true;

    const handleProfileFailure = (error, { preserveReturnUrl = false } = {}) => {
      if (typeof datadogRum?.addError === "function") {
        datadogRum.addError(error, {
          route: router.asPath,
          source: "auth-guard",
        });
      }

      setProfileState({
        user: null,
        isLoading: false,
        error,
      });

      if (
        router.pathname !== "/auth/login" &&
        router.pathname !== "/logout"
      ) {
        window.location.href = preserveReturnUrl
          ? buildLogoutUrl(router.asPath)
          : "/logout";
      }
    };

    loadUserProfile()
      .then((result) => {
        if (!isMounted) {
          return;
        }

        if (result.kind === "authenticated") {
          setProfileState({
            user: result.user,
            isLoading: false,
            error: null,
          });
          return;
        }

        handleProfileFailure(result.error, {
          preserveReturnUrl: result.kind === "unauthorized",
        });
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }

        handleProfileFailure(error);
      });

    return () => {
      isMounted = false;
    };
  }, [router]);

  useEffect(() => {
    if (!router.isReady || !isBlockedBySupplierTos) return;

    void router.push({
      pathname: "/accept-tos",
    });
  }, [isBlockedBySupplierTos, router]);

  useEffect(() => {
    if (!profileState.user) {
      return;
    }
    startSignInSession(profileState.user);
  }, [profileState.user]);

  // if auth initialized with a valid user show protected page
  if (profileState.user && !isBlockedBySupplierTos) {
    return renderProtectedChildren(children, profileState);
  }

  /* otherwise don't return anything, will do a redirect from useEffect */
  return null;
}
